import { supabase } from '../lib/supabaseClient'

/**
 * Ensures a profile row exists in the `profiles` table for the given user.
 * This acts as a safety net in case the database trigger (`handle_new_user`)
 * fails or doesn't exist.
 *
 * Uses upsert to avoid conflicts if the profile already exists.
 */
export async function ensureProfile(
  userId: string,
  metadata?: {
    firstName?: string
    lastName?: string
    fullName?: string
    email?: string
  },
): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('[ensureProfile] Checking/creating profile for user:', userId)

    // First, check if profile already exists
    const { data: existing, error: fetchError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .maybeSingle()

    if (fetchError) {
      console.error(
        '[ensureProfile] Error checking existing profile:',
        fetchError,
      )
      // Don't throw — try to upsert anyway
    }

    if (existing) {
      console.log('[ensureProfile] Profile already exists, skipping creation')
      return { success: true }
    }

    // Profile doesn't exist — create it via upsert
    console.log('[ensureProfile] No profile found, creating one...')

    const profileData: Record<string, any> = {
      id: userId,
      is_pro: false,
      scan_credits: 1, // New users get 1 free scan
    }

    // Add optional metadata fields if the profiles table supports them
    if (metadata?.firstName) {
      profileData.first_name = metadata.firstName
    }
    if (metadata?.lastName) {
      profileData.last_name = metadata.lastName
    }
    if (metadata?.fullName) {
      profileData.full_name = metadata.fullName
    }
    if (metadata?.email) {
      profileData.email = metadata.email
    }

    const { error: upsertError } = await supabase
      .from('profiles')
      .upsert(profileData, {
        onConflict: 'id',
        ignoreDuplicates: true,
      })

    if (upsertError) {
      console.error('[ensureProfile] Upsert error:', upsertError)
      // If the upsert fails due to unknown columns, try with minimal data
      if (
        upsertError.message?.includes('column') ||
        upsertError.code === '42703'
      ) {
        console.log('[ensureProfile] Retrying with minimal profile data...')
        const { error: minimalError } = await supabase.from('profiles').upsert(
          {
            id: userId,
            is_pro: false,
            scan_credits: 1, // New users get 1 free scan
          },
          {
            onConflict: 'id',
            ignoreDuplicates: true,
          },
        )

        if (minimalError) {
          console.error(
            '[ensureProfile] Minimal upsert also failed:',
            minimalError,
          )
          return { success: false, error: minimalError.message }
        }
      } else {
        return { success: false, error: upsertError.message }
      }
    }

    console.log('[ensureProfile] Profile created successfully')
    return { success: true }
  } catch (err: any) {
    console.error('[ensureProfile] Unexpected error:', err)
    return {
      success: false,
      error: err.message || 'Unknown error creating profile',
    }
  }
}
